import { apiRequest } from "@/api/base";

/**
 * Shared contract definitions and helper functions for managing custom
 * domains associated with user sites.
 */

/**
 * Representation of a custom domain record as returned by the backend.
 */
export interface CustomDomain {
  id: string;
  siteId: string;
  hostname: string;
  status: 'pending' | 'active' | 'error' | 'dns_not_found';
  scdDomainId?: string;
  scdStatus?: 'pending_check' | 'dns_records_found' | 'dns_records_not_found';
  tlsReady?: boolean;
  live?: boolean;
  cname: {
    type: 'CNAME';
    name: string;
    value: 'in.saascustomdomains.com';
  };
  createdAt: string;
  updatedAt: string;
  errorMessage?: string;
}

/**
 * Payload required to register a new custom domain.
 */
export interface CreateDomainRequest {
  hostname: string;
  siteId: string;
}

/**
 * Register a new custom domain for a site.
 *
 * @param data - Hostname and owning site identifier.
 */
export const createCustomDomain = async (data: CreateDomainRequest): Promise<CustomDomain> => {
  const response = await apiRequest<CustomDomain>(
    `/api/domains`,
    "POST",
    data,
    true
  );
  return response;
};

/**
 * Retrieve all custom domains associated with a given site.
 *
 * @param siteId - Identifier of the site to inspect.
 */
export const getDomainsBySiteId = async (siteId: string): Promise<CustomDomain[]> => {
  try {
    const response = await apiRequest<CustomDomain[]>(
      `/api/domains/site/${siteId}`,
      "GET",
      undefined,
      true
    );
    return response;
  } catch (error) {
    console.error(`Failed to fetch domains for siteId ${siteId}:`, error);
    return [];
  }
};

/**
 * Lookup a domain by hostname via the public endpoint.
 *
 * @param hostname - The fully qualified domain name to resolve.
 */
export const getDomainByHostname = async (hostname: string): Promise<CustomDomain | null> => {
  try {
    const response = await apiRequest<CustomDomain>(
      `/api/domains/hostname/${hostname}`,
      "GET",
      undefined,
      false // No authentication required for public domain lookup
    );
    return response;
  } catch (error) {
    console.error(`Failed to fetch domain for hostname ${hostname}:`, error);
    return null;
  }
};

/**
 * Remove an existing custom domain configuration.
 *
 * @param domainId - Identifier of the domain record.
 */
export const deleteDomain = async (domainId: string): Promise<void> => {
  await apiRequest(
    `/api/domains/${domainId}`,
    "DELETE",
    undefined,
    true
  );
};

/**
 * Trigger a domain verification check and return the updated record.
 *
 * @param domainId - Identifier of the domain record.
 */
export const checkDomainStatus = async (domainId: string): Promise<CustomDomain> => {
  const response = await apiRequest<CustomDomain>(
    `/api/domains/${domainId}/check`,
    "POST",
    undefined,
    true
  );
  return response;
};
